import React, { FunctionComponent, useContext, useMemo, useState } from "react";
import { 
  Typography, 
  Collapse, 
  IconButton, 
  Button, 
  TextField,
  TableCell,
  TableRow,
  Checkbox
} from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import styles from "./NewTickets.module.css";
import CustomDialog from "../../components/shared-components/dialog/CustomDialog";
import DynamicTable, { ColumnDef } from "../../components/shared-components/table/DynamicTable";
import { NewListTicket } from "./NewTicketsTypes";
import moment from "moment-timezone";
import { useMutation } from "@apollo/client";
import { EDIT_TICKET_PRICE_BY_SUPER_USER, NEW_DELIST_TICKETS } from "./api/TicketsQuery";
import ToasterWithMessage from "../../components/shared-components/toaster";
import { globalContext } from "../../Provider/AppProvider";
import { useForm, FieldValues } from "react-hook-form";
import { emailRegexPattern, formatNumberWithCommas } from "../../helper";
import { newTicketsEventHeaderLabelForSuperUser } from "./newTicketsEventHeaderLabel";

// Helper for status colors (kept here or moved to a helper file if reused elsewhere)
const getStatusColor = (status: string) => {
  switch (status) {
    case 'List': 
    case 'Listed': return { bg: '#e0e7ff', color: '#000000', border: '#c7d2fe' };
    case 'Sold': return { bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' };
    case 'Delist':
    case 'DelistInProgress':
    case 'Delisted': return { bg: '#fee2e2', color: '#b91c1c', border: '#fecaca' };
    default: return { bg: '#f3f4f6', color: '#374151', border: '#e5e7eb' };
  }
};

const formatTicketDateTime = (dateStr: string) => {
    if (!dateStr) return { date: '-', time: '-' };
    const dt = moment.utc(dateStr).tz('America/Chicago');
    return {
        date: dt.format("MM/DD/YYYY"),
        time: dt.format("h:mm A")
    };
};

interface NewListTicketsProps {
    tickets: NewListTicket[];
    sortColumn?: string | null;
    sortDirection?: "asc" | "desc";
    onRequestSort?: (columnId: string) => void;
}

interface EmailFormValues extends FieldValues {
    email: string;
    confirmEmail: string;
}

const NewListTickets: FunctionComponent<NewListTicketsProps> = ({
  tickets,
  sortColumn = null,
  sortDirection = "asc",
  onRequestSort,
}) => {
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [tempPrice, setTempPrice] = useState<string>("");
  const [selectedSeats, setSelectedSeats] = useState<Record<string, string[]>>({}); // ticket_id -> tp_ids array
  
  // Modal State
  const [openDelistModal, setOpenDelistModal] = useState(false);
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [activeTicket, setActiveTicket] = useState<NewListTicket | null>(null);

  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm<EmailFormValues>({
    defaultValues: { email: "", confirmEmail: "" },
    mode: "onTouched"
  });
  
  const {error,success,info} = ToasterWithMessage();
  const {globalState,handleGlobalState} = useContext<any>(globalContext);

  const listedTicketColumns = useMemo((): ColumnDef[] => {
    const eventLabel = newTicketsEventHeaderLabelForSuperUser(
      globalState?.profile?.userRole
    );
    return [
      { id: "expand", label: "", className: styles.headerCellExpand },
      {
        id: "eventName",
        label: eventLabel,
        style: { color: "rgb(198 198 231)" },
        className: styles.headerCell,
      },
      {
        id: "section",
        label: "SECTION",
        style: { color: "rgb(198 198 231)" },
        className: styles.headerCell,
      },
      {
        id: "row",
        label: "ROW",
        style: { color: "rgb(198 198 231)" },
        className: styles.headerCell,
      },
      {
        id: "seat",
        label: "SEAT #S",
        style: { color: "rgb(198 198 231)" },
        className: styles.headerCell,
      },
      {
        id: "status",
        label: "STATUS",
        style: { color: "rgb(198 198 231)", textAlign: "center" },
        className: `${styles.headerCell} ${styles.headerCellCenter}`,
      },
      {
        id: "price",
        label: "LISTED PRICE",
        style: { color: "rgb(198 198 231)", textAlign: "center" },
        className: `${styles.headerCell} ${styles.headerCellCenter}`,
      },
    ];
  }, [globalState?.profile?.userRole]);

    const [editTicketPrice, {loading:editTicketPriceLoading}] = useMutation(EDIT_TICKET_PRICE_BY_SUPER_USER, {
      fetchPolicy: "network-only",
      onCompleted:(data)=>{
        const message = data?.editTicketPriceBySuperUser?.message;
        const normalized = message.toLowerCase();
        if (normalized.includes('unable') && message.includes('successfully')) {
          info(message);
        }else if(normalized.includes('successfully')){
          success(message);
        }else if(normalized.includes('failed')){
          error(message);
        }else{
          info(message);
        }
        const tempState ={...globalState};
        tempState.ticketUpdate.listPrice = true ;
        handleGlobalState(tempState);
      },
      onError:(err)=>{
        error(err?.message?.includes("not a valid json response from webhook") ? "Not a valid response from webhook" : err?.message || "Not a valid response from webhook" );
      }
    });

  const [delistTickets, {loading:delistTicketsLoading}] = useMutation(NEW_DELIST_TICKETS,{
      fetchPolicy : 'no-cache',
      onCompleted:(data) =>{
        const message = data?.delistTickets?.message;
        const normalized = message.toLowerCase();
        if (normalized.includes('failed') && message.includes('successfully')) {
          info(message);
        }else if(normalized.includes('successfully')){
          success(message);
        }else if(normalized.includes('failed')){
          error(message);
        }else{
          info(message);
        }
        const tempState = {...globalState};
        tempState.ticketUpdate.delistTicket  = true
        handleGlobalState(tempState);
        closeModals();
      },
      onError: (err) =>{
        error(err?.message?.includes("not a valid json response from webhook") ? "Not a valid response from webhook" : err?.message || "Not a valid response from webhook" );
        closeModals();
      }
    })    

    
  const toggleRow = (id: string) => {
    setExpandedRows(prev => 
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  // We use ticket_id (group id) to manage editing state for the group row
  const startEditing = (id: string, currentPrice: number) => {
    setEditingPriceId(id);
    setTempPrice(currentPrice.toString());
  };

  const cancelEditing = () => {
    setEditingPriceId(null);
    setTempPrice("");
  };

  const savePrice = async (id: string, tpIds: string[]) => {
    console.log(`Saving price ${tempPrice} for ticket group ${id}, tp_ids: ${tpIds.join(', ')}`);
    // Here we would call the mutation to update price for all tp_ids
    await editTicketPrice({
      variables:{
        tp_ids:tpIds,
        tp_list_price:Number(tempPrice),
      }
    })
    setEditingPriceId(null);
  };

  // Seat Selection
  const handleSeatClick = (ticketId: string, tpId: string) => {
    setSelectedSeats(prev => {
        const currentSelected = prev[ticketId] || [];
        const isSelected = currentSelected.includes(tpId);
        const newSelected = isSelected 
            ? currentSelected.filter(id => id !== tpId)
            : [...currentSelected, tpId];
        return { ...prev, [ticketId]: newSelected };
    });
  };

  const handleSelectAll = (ticketId: string, allTpIds: string[]) => {
      setSelectedSeats(prev => ({ ...prev, [ticketId]: allTpIds }));
  };

  const handleClear = (ticketId: string) => {
    setSelectedSeats(prev => ({ ...prev, [ticketId]: [] }));
  };

  // Delist Logic
  const handleRequestDelist = (ticket: NewListTicket) => {
      setActiveTicket(ticket);
      setOpenDelistModal(true);
  };

  const confirmDelist = () => {
      const rowId = activeTicket?.grouped_ticket_data?.[0]?.tp_id || '';
      console.log("Delisting tp_ids:", selectedSeats[rowId]);
      setOpenDelistModal(false);
      setOpenEmailModal(true);
  };

  const closeModals = () => {
      reset();
      setOpenDelistModal(false);
      setOpenEmailModal(false);
      if (activeTicket) {
          handleClear(activeTicket.grouped_ticket_data?.[0]?.tp_id);
      }
      setActiveTicket(null);
  };

  const onEmailSubmit = async(data: EmailFormValues) => {
      const rowId = activeTicket?.grouped_ticket_data?.[0]?.tp_id || '';
      console.log("Submitting email:", data.email, selectedSeats[rowId]);

      await delistTickets({  
        variables: {
          ticketPlacementIds: selectedSeats[rowId],
          requestedEmailId: data.email,
        },
      });
  };

  return (
    <>
    <DynamicTable
      columns={listedTicketColumns}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onRequestSort={onRequestSort}
    >
            {tickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={listedTicketColumns.length} className={styles.emptyTableMessage}>
                  No Data Found
                </TableCell>
              </TableRow>
            ) : tickets.map((ticket) => {
                // Using ticket_id as unique key for the row
                const rowId = ticket?.grouped_ticket_data?.[0]?.tp_id;
                const isExpanded = expandedRows.includes(rowId);
                const isEditing = editingPriceId === rowId;
                const statusStyle = getStatusColor(ticket.tp_status);
                const currentSelected = selectedSeats[rowId] || [];
                
                // Get all tp_ids for this group
                const allTpIds = ticket.grouped_ticket_data.map(g => g.tp_id);

                // Format date and time
                const { date, time } = formatTicketDateTime(ticket.event_date_time);

                return (
                    <React.Fragment key={ticket?.grouped_ticket_data?.[0]?.tp_id}>
                        <TableRow 
                            className={`${styles.tableRow} ${isExpanded ? styles.rowExpanded : ''}`}
                            onClick={() => toggleRow(rowId)}
                        >
                            <TableCell className={styles.bodyCell}>
                                <IconButton size="small">
                                    {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </IconButton>
                            </TableCell>
                            <TableCell className={styles.bodyCell} component="th" scope="row">
                                <Typography variant="subtitle2" className={styles.eventName}>{ticket.event_name}</Typography>
                                <Typography variant="caption" className={styles.eventDate}>
                                    {date} • {time}
                                </Typography>
                            </TableCell>
                            <TableCell className={styles.bodyCell}>{ticket.section}</TableCell>
                            <TableCell className={styles.bodyCell}>{ticket.row}</TableCell>
                            <TableCell className={styles.bodyCell}>{ticket.seat_range}</TableCell>
                            <TableCell className={styles.bodyCell} align="center">
                                <span className={styles.statusPill} style={{ backgroundColor: statusStyle.bg, color: statusStyle.color, borderColor: statusStyle.border }}>
                                    {  ticket?.tp_status === "List"
                                        ? "Listed"
                                        : ticket?.tp_status === "DelistInProgress"
                                        ? "Delist Requested" 
                                        : "" }
                                </span>
                            </TableCell>
                            <TableCell className={styles.bodyCell} align="center">
                                {isEditing ? (
                                    <div className={styles.editPriceWrapper}>
                                        <TextField 
                                            size="small" 
                                            value={tempPrice} 
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if(val === '0' || Number(val) < 0) return;
                                                setTempPrice(val)
                                            }}
                                            className={styles.priceInput}
                                            autoFocus
                                            type="number"
                                            onClick={(e) => e.stopPropagation()}
                                            InputProps={{ style: { fontSize: 13, padding: 0, borderRadius: '10px' } }}
                                        />
                                        <Button size="small" variant="contained" color="success" onClick={(e) => { e.stopPropagation(); savePrice(rowId, allTpIds); }} className={styles.confirmBtn} disabled={editTicketPriceLoading || !tempPrice}>Confirm</Button>
                                        <Button size="small" variant="contained" color="error" onClick={(e) => { e.stopPropagation(); cancelEditing(); }} className={styles.cancelBtn}>Cancel</Button>
                                    </div>
                                ) : (
                                    <div className={styles.displayPriceWrapper}>
                                        <span className={styles.priceText}>{ticket.tp_list_price ? `$${ticket.tp_list_price}`: '-'}</span>
                                        <Button 
                                            size="small" 
                                            className={styles.editLink} 
                                            onClick={(e) => { e.stopPropagation(); startEditing(rowId, ticket.tp_list_price); }}
                                        >
                                            Edit
                                        </Button>
                                    </div>
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell style={{ padding: 0, border: 'none' }} colSpan={7}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <div className={styles.expandedContent}>
                                        <div className={styles.expandedHeader}>
                                            <div className={styles.ticketGroupDetails}>
                                                <Typography variant="caption" className={styles.detailsLabel}>Ticket Group Details</Typography>
                                                <div className={styles.detailsInfo}>
                                                    <strong>Section {ticket.section}</strong> <span className={styles.greyDotText}>•</span> <strong>Row {ticket.row}</strong> <span className={styles.greyDotText}>•</span> <span className={styles.greyText}>Seats {ticket.seat_range}</span>
                                                </div>
                                            </div>
                                            <div className={styles.expandedActions}>
                                                <Button size="small" className={styles.textBtn} onClick={() => handleSelectAll(rowId, allTpIds)}>Select all</Button>
                                                <Button size="small" className={styles.textBtn} onClick={() => handleClear(rowId)}>Clear</Button>
                                                <Button 
                                                    variant="contained" 
                                                    className={`${styles.actionBtn} ${currentSelected.length === 0 ? styles.disabledBtn : ''}`} 
                                                    disabled={currentSelected.length === 0}
                                                    onClick={() => handleRequestDelist(ticket)}
                                                >
                                                    {`Request Delist ${currentSelected.length > 0 ? `(${currentSelected.length})` : ''}`}
                                                </Button>
                                            </div>
                                        </div>
                                        
                                        <div className={styles.seatsContainer}>
                                            {ticket.grouped_ticket_data.map(seat => {
                                                const isSelected = currentSelected.includes(seat.tp_id);
                                                return (
                                                    <div 
                                                        key={seat.tp_id} 
                                                        className={`${styles.seatCard} ${isSelected ? styles.seatCardSelected : ''}`}
                                                        onClick={() => handleSeatClick(rowId, seat.tp_id)}
                                                    >
                                                        <div className={`${styles.seatLeft} ${isSelected ? styles.seatLeftSelected : ''}`}>
                                                            <Typography variant="body2" className={styles.seatLabel}>Seat {seat.seat_no}</Typography>
                                                            <Typography variant="caption" className={styles.seatStatus}> 
                                                                {  seat?.tp_status === "List"
                                                                    ? "Listed"
                                                                    : seat?.tp_status === "DelistInProgress"
                                                                    ? "Delist Requested" 
                                                                    : "" }
                                                            </Typography>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </Collapse>
                            </TableCell>
                        </TableRow>
                    </React.Fragment>
                );
            })}
    </DynamicTable>

      {/* Delist Confirmation Modal */}
      <CustomDialog 
        open={openDelistModal} 
        onClose={closeModals} 
        title="Are you sure you want to delist these tickets?"
        subtitle={`You are about to request delisting for ${selectedSeats[activeTicket?.grouped_ticket_data?.[0]?.tp_id || '']?.length || 0} ticket(s).`}
        paperProps={{ sx: { borderRadius: '15px' } }}
        actions={
            <>
                <Button disableElevation variant="outlined" onClick={closeModals} className={styles.dialogCancelBtn}>No</Button>
                <Button disableElevation variant="contained" onClick={confirmDelist} className={styles.dialogConfirmBtn}>Yes</Button>
            </>
        }
      >
          {activeTicket && (
              <div className={styles.modalTicketCard}>
                    <div className={styles.modalTicketHeader}>
                        <div className={styles.modalHeaderLeft}>
                            <Typography variant="subtitle1" className={styles.modalEventName}>
                                {activeTicket.event_name}
                            </Typography>
                            <Typography variant="caption" className={styles.modalEventDate}>
                                <span>Date: {formatTicketDateTime(activeTicket.event_date_time).date}</span> <span className={styles.greyDotText}>•</span> <span>Time: {formatTicketDateTime(activeTicket.event_date_time).time}</span>
                            </Typography>
                        </div>
                        <span className={styles.modalSelectedCount}>{selectedSeats[activeTicket.grouped_ticket_data?.[0]?.tp_id]?.length} selected</span>
                    </div>
                    
                    <div className={styles.modalTicketDetails}>
                        <div className={styles.detailBoxItem}>
                            <span className={styles.detailLabel}>Section</span>
                            <span className={styles.detailValue}>{activeTicket.section}</span>
                        </div>
                        <div className={styles.detailBoxItem}>
                            <span className={styles.detailLabel}>Row</span>
                            <span className={styles.detailValue}>{activeTicket.row}</span>
                        </div>
                        <div className={styles.detailBoxItem}>
                            <span className={styles.detailLabel}>Seats</span>
                            <span className={styles.detailValue}>{activeTicket.seat_range}</span>
                        </div>
                    </div>
              </div>
          )}
      </CustomDialog>

        {/* Email Confirmation Modal */}
        <CustomDialog 
            open={openEmailModal} 
            onClose={closeModals} 
            title="Please provide the email you would like your tickets returned to"
            subtitle="Enter and confirm the email address."
            showCloseIcon={true}
            paperProps={{ sx: { maxWidth: '510px', borderRadius: '15px' } }}
            actions={
                <>
                  <Button variant="outlined" onClick={closeModals} className={styles.dialogCancelBtn}>Cancel</Button>
                  <Button variant="contained" onClick={handleSubmit(onEmailSubmit)} className={styles.dialogConfirmBtn} disabled={delistTicketsLoading}>{delistTicketsLoading ? 'Submitting...' : 'Submit'}</Button>
                </>
            }
        >
              <div className={styles.formGroup}>
                  <Typography className={styles.inputLabel}>Email</Typography>
                  <TextField 
                      fullWidth 
                      variant="outlined" 
                      placeholder="name@company.com"
                      className={styles.modalInput}
                      {...register("email", { 
                        required: "Email is required",
                        pattern: {
                            value: emailRegexPattern,
                            message: "Enter valid email"
                        }
                      })}
                      error={Boolean(errors.email)}
                      helperText={errors.email?.message as string}
                  />
              </div>
              <div className={styles.formGroup}>
                    <Typography className={styles.inputLabel}>Confirm email</Typography>
                  <TextField 
                      fullWidth 
                      variant="outlined" 
                      placeholder="name@company.com"
                      className={styles.modalInput}
                      {...register("confirmEmail", { 
                        required: "Confirm Email is required",
                        pattern: {
                            value: emailRegexPattern,
                            message: "Enter valid email"
                        },  
                        validate: (val: string) => {
                            if (watch("email") !== val) {
                                return "Emails do not match";
                            }
                        }
                      })}
                      error={Boolean(errors.confirmEmail)}
                      helperText={errors.confirmEmail?.message as string}
                  />
              </div>
      </CustomDialog>
    </>
  );
};

export default NewListTickets;
